import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SubjectModel, SubjectResponse } from "model";

import { fetchRequestAttribute } from "~utils";

import { computeInitialSubjectSelected } from "./utils.ts";

export const useSubjects = (initialSubjectSelected: string[], testData?: SubjectModel[]) => {
  // validatedInitialData here takes the user's preferred subjects and grades
  // and coerces it into the list that the Subject renderer will accept. Since
  // there are many existing records of grades and subjects being transacted
  // in that don't fit the list, these values will get sorted into "Unknown"

  const [validatedInitialData, setValidatedInitialData] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);

  const { data: subjectData } = useQuery({
    queryKey: ["fetchRequestAttributeSubjects"],
    queryFn: async () => {
      const data = await fetchRequestAttribute<SubjectResponse>({ key: "subjects" });
      const subjectData = data?.data?.options?.map((subject) => new SubjectModel(subject)) || [];

      const validInitialData = computeInitialSubjectSelected(subjectData, initialSubjectSelected);
      setValidatedInitialData(validInitialData);
      setSelected(validInitialData);
      return subjectData;
    },
    enabled: !testData, // if testData exists, do not execute the hook api call
    initialData: [],
  });

  // Update selected subjects when initialSubjectSelected changes (e.g., teacher selection)
  useEffect(() => {
    if (subjectData?.length > 0) {
      const validInitialData = computeInitialSubjectSelected(subjectData, initialSubjectSelected);
      setValidatedInitialData(validInitialData);
      setSelected(validInitialData);
    }
  }, [initialSubjectSelected, subjectData]);

  if (testData) return { selected, setSelected, validatedInitialData, subjectData: testData };
  return { selected, setSelected, validatedInitialData, subjectData };
};
